import { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown, X } from 'lucide-react';
import type { ProjectMember } from '../../services/projectService';
import type { ProjectIssueType } from '../../hooks/useProjectIssueTypes';

interface QuickFilterProps {
  members: ProjectMember[];
  workflowStatuses: { statusId: string; name: string }[];
  value: QuickFilterState;
  onChange: (value: QuickFilterState) => void;
  projectIssueTypes?: ProjectIssueType[];  // CHG-052
}

export interface QuickFilterState {
  types: string[];
  statuses: string[];
  assignees: string[];
  priorities: string[];
}

export const EMPTY_QUICK_FILTER: QuickFilterState = {
  types: [],
  statuses: [],
  assignees: [],
  priorities: [],
};

/** @deprecated CHG-052: projectIssueTypes prop 폴백 */
const FALLBACK_ISSUE_TYPES = [
  { value: 'Epic', label: 'Epic' },
  { value: 'Story', label: 'Story' },
  { value: 'Task', label: 'Task' },
  { value: 'Subtask', label: 'Subtask' },
  { value: 'Bug', label: 'Bug' },
];

const PRIORITIES = [
  { value: 'BLOCKER', label: 'Blocker', color: 'bg-black text-white' },
  { value: 'CRITICAL', label: 'Critical', color: 'bg-red-600 text-white' },
  { value: 'HIGH', label: 'High', color: 'bg-red-100 text-red-700' },
  { value: 'MEDIUM', label: 'Medium', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'LOW', label: 'Low', color: 'bg-blue-100 text-blue-700' },
];

export default function QuickFilter({ members, workflowStatuses, value, onChange, projectIssueTypes }: QuickFilterProps) {
  const ISSUE_TYPES = projectIssueTypes?.map((t) => ({ value: t.name, label: t.name })) ?? FALLBACK_ISSUE_TYPES;
  const hasFilter =
    value.types.length > 0 ||
    value.statuses.length > 0 ||
    value.assignees.length > 0 ||
    value.priorities.length > 0;

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <MultiSelectDropdown
        label="유형"
        options={ISSUE_TYPES.map((t) => ({ value: t.value, label: t.label }))}
        selected={value.types}
        onChangeSelected={(types) => onChange({ ...value, types })}
      />
      <MultiSelectDropdown
        label="상태"
        options={workflowStatuses.map((ws) => ({ value: ws.name, label: ws.name }))}
        selected={value.statuses}
        onChangeSelected={(statuses) => onChange({ ...value, statuses })}
      />
      <MultiSelectDropdown
        label="담당자"
        options={[
          { value: '__unassigned__', label: '미할당' },
          ...members.map((m) => ({ value: m.user.id, label: m.user.name })),
        ]}
        selected={value.assignees}
        onChangeSelected={(assignees) => onChange({ ...value, assignees })}
      />
      <MultiSelectDropdown
        label="우선순위"
        options={PRIORITIES.map((p) => ({ value: p.value, label: p.label }))}
        selected={value.priorities}
        onChangeSelected={(priorities) => onChange({ ...value, priorities })}
      />
      {hasFilter && (
        <button
          onClick={() => onChange(EMPTY_QUICK_FILTER)}
          className="flex items-center gap-1 px-2 py-1 text-sm text-gray-500 hover:text-gray-700"
        >
          <X size={12} />
          초기화
        </button>
      )}
    </div>
  );
}

// --- Multi-select dropdown component ---
export function MultiSelectDropdown({
  label,
  options,
  selected,
  onChangeSelected,
}: {
  label: string;
  options: { value: string; label: string }[];
  selected: string[];
  onChangeSelected: (selected: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (ref.current && !ref.current.contains(e.target as Node)) {
      setOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  const toggle = (val: string) => {
    if (selected.includes(val)) {
      onChangeSelected(selected.filter((s) => s !== val));
    } else {
      onChangeSelected([...selected, val]);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1 px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50 transition-colors ${
          selected.length > 0
            ? 'border-primary-500 bg-primary-50 text-primary-700'
            : 'border-gray-300 text-gray-700'
        }`}
      >
        {label}
        {selected.length > 0 && (
          <span className="ml-1 px-1.5 py-0.5 text-sm bg-primary-500 text-white rounded-full">
            {selected.length}
          </span>
        )}
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-30 py-1 max-h-60 overflow-y-auto">
          {options.map((opt) => (
            <label
              key={opt.value}
              className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selected.includes(opt.value)}
                onChange={() => toggle(opt.value)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              {opt.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Helpers to build TQL from quick filter ---
export function buildTqlFromQuickFilter(filter: QuickFilterState): string {
  const parts: string[] = [];

  if (filter.types.length > 0) {
    if (filter.types.length === 1) {
      parts.push(`type = ${filter.types[0]}`);
    } else {
      parts.push(`type IN (${filter.types.join(', ')})`);
    }
  }

  if (filter.statuses.length > 0) {
    if (filter.statuses.length === 1) {
      parts.push(`status = ${filter.statuses[0]}`);
    } else {
      parts.push(`status IN (${filter.statuses.join(', ')})`);
    }
  }

  if (filter.assignees.length > 0) {
    const unassigned = filter.assignees.includes('__unassigned__');
    const userIds = filter.assignees.filter((a) => a !== '__unassigned__');
    if (unassigned && userIds.length === 0) {
      parts.push('assignee IS EMPTY');
    } else if (userIds.length === 1 && !unassigned) {
      parts.push(`assignee = "${userIds[0]}"`);
    } else if (userIds.length > 0) {
      parts.push(`assignee IN (${userIds.map((u) => `"${u}"`).join(', ')})`);
    }
  }

  if (filter.priorities.length > 0) {
    if (filter.priorities.length === 1) {
      parts.push(`priority = ${filter.priorities[0]}`);
    } else {
      parts.push(`priority IN (${filter.priorities.join(', ')})`);
    }
  }

  return parts.join(' AND ');
}
